import { apiClient } from './api-client';

export interface UserRoleItem {
  id: string;
  name: string;
  slug: string;
}

export interface UserDepartmentItem {
  id: string;
  name: string;
  code: string;
}

export interface UserManagerItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  employeeCode: string | null;
}

export interface OrgUser {
  id: string;
  tenantId: string;
  email: string;
  username: string | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  alternatePhone: string | null;
  alternateEmail: string | null;
  employeeCode: string | null;
  designation: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postalCode: string | null;
  emergencyContactName: string | null;
  emergencyContactPhone: string | null;
  notes: string | null;
  isActive: boolean;
  /** D-099/D-100 — declared shared mailbox, and how many use it (null when not shared). */
  isSharedEmailAccount: boolean;
  sharedEmailUserCount: number | null;
  mustChangePassword: boolean;
  profilePhotoUrl: string | null;
  department: UserDepartmentItem | null;
  reportingManager: UserManagerItem | null;
  roles: UserRoleItem[];
  lastLoginAt: string | null;
  passwordChangedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserListResponse {
  items: OrgUser[];
  page: number;
  pageSize: number;
  total: number;
}

export interface CreateUserPayload {
  email?: string;
  password: string;
  firstName: string;
  lastName: string;
  username?: string;
  phone?: string;
  employeeCode?: string;
  designation?: string;
  departmentId?: string;
  reportingManagerId?: string;
  roleIds: string[];
  /** D-099 — allow this address to be used by more than one employee. */
  isSharedEmailAccount?: boolean;
}

export type IdentityField = 'email' | 'username' | 'phone' | 'employeeCode';

export interface IdentityAvailability {
  available: boolean;
  reason: 'TAKEN' | null;
  /**
   * Present only for `email`, and only when the clash is with staff in this
   * tenant — i.e. exactly when ticking "Shared email account" would resolve it.
   * Null for a customer/portal-contact clash, which sharing cannot fix.
   */
  sharedEmail: { isShared: boolean; userCount: number } | null;
}

export interface SharedMailboxUser {
  id: string;
  firstName: string;
  lastName: string;
  username: string | null;
  employeeCode: string | null;
  roles: string[];
  isActive: boolean;
  lastLoginAt: string | null;
}

export interface UpdateUserPayload {
  /** D-100 — Edit now exposes the address, the shared-mailbox declaration and the code. */
  email?: string;
  isSharedEmailAccount?: boolean;
  employeeCode?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  phone?: string;
  alternatePhone?: string;
  alternateEmail?: string;
  designation?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  notes?: string;
  departmentId?: string | null;
  reportingManagerId?: string | null;
}

export interface UserListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  departmentId?: string;
  roleSlug?: string;
  isActive?: boolean;
  includeDeleted?: boolean;
  /** Only users missing username, mobile, or employee code (D-074 grandfathered accounts). */
  incompleteIdentity?: boolean;
}

/** One-time result of an admin-initiated password reset. */
export interface AdminPasswordReset {
  username: string | null;
  employeeCode: string | null;
  temporaryPassword: string;
}

export const userManagementService = {
  list: (params: UserListParams): Promise<UserListResponse> =>
    apiClient.get<UserListResponse>('/users', { params }).then((r) => r.data),

  get: (id: string): Promise<OrgUser> => apiClient.get<OrgUser>(`/users/${id}`).then((r) => r.data),

  /**
   * D-100 — advisory availability check for the Add/Edit User form.
   *
   * Advisory ONLY. The backend validates again on submit and is the authority;
   * this exists so the operator finds out while typing rather than after
   * pressing Create. A failure here must never block the form — the caller
   * treats an error as "unknown" and lets the submit-time 409 decide.
   */
  checkAvailability: (params: {
    field: IdentityField;
    value: string;
    excludeUserId?: string;
  }): Promise<IdentityAvailability> =>
    apiClient
      .get<IdentityAvailability>('/users/identity/availability', { params })
      .then((r) => r.data),

  /** D-100 — the other individuals using this user's email address. */
  sharedMailboxUsers: (id: string): Promise<SharedMailboxUser[]> =>
    apiClient
      .get<SharedMailboxUser[]>(`/users/${id}/shared-mailbox`)
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  create: (payload: CreateUserPayload): Promise<OrgUser> =>
    apiClient.post<OrgUser>('/users', payload).then((r) => r.data),

  update: (id: string, payload: UpdateUserPayload): Promise<OrgUser> =>
    apiClient.patch<OrgUser>(`/users/${id}`, payload).then((r) => r.data),

  remove: (id: string): Promise<void> => apiClient.delete(`/users/${id}`).then(() => undefined),

  suspend: (id: string): Promise<OrgUser> =>
    apiClient.patch<OrgUser>(`/users/${id}/suspend`, {}).then((r) => r.data),

  activate: (id: string): Promise<OrgUser> =>
    apiClient.patch<OrgUser>(`/users/${id}/activate`, {}).then((r) => r.data),

  assignRoles: (id: string, roleIds: string[]): Promise<OrgUser> =>
    apiClient.put<OrgUser>(`/users/${id}/roles`, { roleIds }).then((r) => r.data),

  /**
   * Issues a temporary password for another employee. The plaintext password is
   * returned exactly once, in this response — it is never retrievable again.
   */
  resetPassword: (id: string): Promise<AdminPasswordReset> =>
    apiClient.post<AdminPasswordReset>(`/users/${id}/reset-password`, {}).then((r) => r.data),

  transferOwnership: (newOwnerUserId: string): Promise<void> =>
    apiClient.post('/users/transfer-ownership', { newOwnerUserId }).then(() => undefined),

  uploadPhoto: (id: string, file: File): Promise<OrgUser> => {
    const form = new FormData();
    form.append('photo', file);
    return apiClient
      .post<OrgUser>(`/users/${id}/profile-photo`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },

  deletePhoto: (id: string): Promise<OrgUser> =>
    apiClient.delete<OrgUser>(`/users/${id}/profile-photo`).then((r) => r.data),
};
